///
/// EarthBrowser
///
/// Created by Matt Giger on 9/9/2026
/// Copyright (c) 2026 Geodata Labs
///

import type { LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

// Semantic color families from the design system (design-system/00-overview.md):
// profile = blue/cyan/teal, security = cyan, subscription = amber/orange,
// privacy = purple/violet, notifications = teal
export type SectionColor = "blue" | "cyan" | "amber" | "purple" | "teal" | "red"

const colorStyles: Record<SectionColor, { glow: string, chip: string, icon: string }> = {
	blue: {
		glow: "bg-gradient-to-br from-blue-600 via-cyan-500 to-teal-500",
		chip: "bg-blue-500/20",
		icon: "text-blue-600 dark:text-blue-400",
	},
	cyan: {
		glow: "bg-gradient-to-br from-cyan-600 via-cyan-500 to-teal-500",
		chip: "bg-cyan-500/20",
		icon: "text-cyan-600 dark:text-cyan-400",
	},
	amber: {
		glow: "bg-gradient-to-br from-amber-600 via-amber-500 to-orange-500",
		chip: "bg-amber-500/20",
		icon: "text-amber-600 dark:text-amber-400",
	},
	purple: {
		glow: "bg-gradient-to-br from-purple-600 via-violet-500 to-purple-500",
		chip: "bg-purple-500/20",
		icon: "text-purple-600 dark:text-purple-400",
	},
	teal: {
		glow: "bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500",
		chip: "bg-teal-500/20",
		icon: "text-teal-600 dark:text-teal-400",
	},
	red: {
		glow: "bg-gradient-to-br from-red-600 via-red-500 to-orange-500",
		chip: "bg-red-500/20",
		icon: "text-red-600 dark:text-red-400",
	},
}

interface SectionHeaderProps {
	icon: LucideIcon
	title: string
	description?: string
	color: SectionColor
	action?: ReactNode
}

/**
 * Section header with gradient-glow icon, per the design system
 * (design-system/02-components.md "Section Header Pattern")
 */
export function SectionHeader({ icon: Icon, title, description, color, action }: SectionHeaderProps) {
	const styles = colorStyles[color]

	return (
		<div className="flex items-center gap-3 mb-3">
			<div className="relative flex-shrink-0">
				<div className={`absolute inset-0 rounded-lg blur-md opacity-60 ${styles.glow}`} />
				<div className={`relative p-2 rounded-lg ${styles.chip}`}>
					<Icon className={`h-4 w-4 ${styles.icon}`} />
				</div>
			</div>
			<div className="flex-1 min-w-0">
				<h3 className="text-base font-bold text-foreground">{title}</h3>
				{description && <p className="text-xs text-muted-foreground">{description}</p>}
			</div>
			{action}
		</div>
	)
}
